import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Tooltip,
  Stack,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { feedbackService, classService, studentService } from '../../services/apiService';
import { showToast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateFormat';
import ConfirmationDialog from '../../components/Common/ConfirmationDialog';

const Feedback = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const drawerWidth = 280;
  
  // Get current user ID
  const currentUserId = user?.id || JSON.parse(localStorage.getItem('user') || '{}')?.id;

  // State
  const [feedback, setFeedback] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Infinite scroll state
  const pageSize = 20;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isAppending, setIsAppending] = useState(false);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false);
  const offsetRef = useRef(0);

  // Filters
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    feedbackId: null,
  });

  // Modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [shareFormData, setShareFormData] = useState({
    class_id: '',
    student_id: '',
    message: '',
  });
  const [editFormData, setEditFormData] = useState({
    message: '',
  });
  const [shareLoading, setShareLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [shareModalStudents, setShareModalStudents] = useState([]);

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await classService.getAll();
        const classesData = response?.data || response || [];
        setClasses(Array.isArray(classesData) ? classesData : []);
      } catch (err) {
        console.error('Failed to load classes:', err);
      }
    };
    fetchClasses();
  }, []);

  // Fetch students when class is selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (!selectedClass) {
        setStudents([]);
        setSelectedStudent('');
        return;
      }

      try {
        const response = await studentService.getAll({ class_id: selectedClass });
        const studentsData = response?.data || response || [];
        setStudents(Array.isArray(studentsData) ? studentsData : []);
        setSelectedStudent(''); // Reset student selection when class changes
      } catch (err) {
        console.error('Failed to load students:', err);
        setStudents([]);
      }
    };

    fetchStudents();
  }, [selectedClass]);

  // Fetch feedback
  const fetchFeedback = useCallback(async (append = false, customOffset = null) => {
    // Prevent parallel or looped calls
    if (fetchingRef.current) {
      return;
    }

    const sentinelEl = sentinelRef.current;
    let observerToRestore = null;
    if (sentinelEl && sentinelEl._io) {
      observerToRestore = sentinelEl._io;
      try { observerToRestore.unobserve(sentinelEl); } catch (e) {}
    }

    try {
      fetchingRef.current = true;
      
      if (!append) {
        setLoading(true);
      } else {
        setIsAppending(true);
      }
      setError(null);

      const nextOffset = customOffset !== null ? customOffset : (append ? offsetRef.current : 0);
      const params = {
        limit: pageSize,
        offset: nextOffset,
      };
      if (selectedClass) params.class_id = selectedClass;
      if (selectedStudent) params.student_id = selectedStudent;

      const response = await feedbackService.getAll(params);
      const feedbackData = response?.data || response || [];
      const fetchedFeedback = Array.isArray(feedbackData) ? feedbackData : [];

      if (append) {
        setFeedback(prev => [...prev, ...fetchedFeedback]);
      } else {
        setFeedback(fetchedFeedback);
      }

      setHasMore(fetchedFeedback.length === pageSize);
      setOffset(nextOffset + fetchedFeedback.length);
      offsetRef.current = nextOffset + fetchedFeedback.length;
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError('Failed to load feedback');
      if (!append) {
        setFeedback([]);
      }
      setHasMore(false);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setIsAppending(false);
        fetchingRef.current = false;
        if (sentinelEl && observerToRestore) {
          try { observerToRestore.observe(sentinelEl); } catch (e) {}
        }
      }, 0);
    }
  }, [selectedClass, selectedStudent, pageSize]);

  // Reset and fetch when filters change
  useEffect(() => {
    setOffset(0);
    offsetRef.current = 0;
    setHasMore(true);
    fetchFeedback(false, 0);
  }, [selectedClass, selectedStudent]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (!first || !first.isIntersecting) return;
      // Only append after the first page is loaded (offset > 0)
      if (hasMore && offsetRef.current > 0 && !isAppending && !loading && !fetchingRef.current) {
        fetchFeedback(true);
      }
    }, { root: null, rootMargin: '300px', threshold: 0 });

    el._io = io;
    io.observe(el);
    return () => {
      try { io.disconnect(); } catch (e) {}
      if (el && el._io === io) { el._io = null; }
    };
  }, [hasMore, isAppending, loading, fetchFeedback]);

  // Fetch students when class is selected in share modal
  useEffect(() => {
    const fetchShareModalStudents = async () => {
      if (!shareFormData.class_id) {
        setShareModalStudents([]);
        return;
      }

      try {
        const response = await studentService.getAll({ class_id: shareFormData.class_id });
        const studentsData = response?.data || response || [];
        setShareModalStudents(Array.isArray(studentsData) ? studentsData : []);
      } catch (err) {
        console.error('Failed to load students for share modal:', err);
        setShareModalStudents([]);
      }
    };

    fetchShareModalStudents();
  }, [shareFormData.class_id]);

  // Handle share feedback
  const handleShareFeedback = async () => {
    if (!shareFormData.class_id || !shareFormData.student_id || !shareFormData.message.trim()) {
      showToast.error('Please fill all fields');
      return;
    }

    try {
      setShareLoading(true);
      await feedbackService.create({
        student_id: shareFormData.student_id,
        message: shareFormData.message.trim(),
      });
      showToast.success('Feedback shared successfully');
      setShareModalOpen(false);
      setShareFormData({ class_id: '', student_id: '', message: '' });
      fetchFeedback();
    } catch (err) {
      console.error('Failed to share feedback:', err);
      showToast.error(err.response?.data?.message || 'Failed to share feedback');
    } finally {
      setShareLoading(false);
    }
  };

  // Handle delete feedback
  const handleDeleteFeedbackClick = (id) => {
    setConfirmDialog({
      open: true,
      feedbackId: id,
    });
  };

  const handleDeleteFeedback = async (id) => {
    try {
      await feedbackService.delete(id);
      showToast.success('Feedback deleted successfully');
      fetchFeedback();
      setConfirmDialog({ open: false, feedbackId: null });
    } catch (err) {
      console.error('Failed to delete feedback:', err);
      showToast.error(err.response?.data?.message || 'Failed to delete feedback');
    }
  };

  // Handle edit feedback
  const handleEditFeedback = (feedbackItem) => {
    setEditingFeedback(feedbackItem);
    setEditFormData({ message: feedbackItem.message });
    setEditModalOpen(true);
  };

  const handleUpdateFeedback = async () => {
    if (!editFormData.message.trim()) {
      showToast.error('Message cannot be empty');
      return;
    }

    try {
      setEditLoading(true);
      await feedbackService.update(editingFeedback.id, {
        message: editFormData.message.trim(),
      });
      showToast.success('Feedback updated successfully');
      setEditModalOpen(false);
      setEditingFeedback(null);
      setEditFormData({ message: '' });
      fetchFeedback();
    } catch (err) {
      console.error('Failed to update feedback:', err);
      showToast.error(err.response?.data?.message || 'Failed to update feedback');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <>
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              mb: 3,
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                }}
              >
                Feedback
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShareModalOpen(true)}
                sx={{ 
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' }
                }}
                size={isMobile ? 'medium' : 'medium'}
              >
                Share Feedback
              </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: { xs: 'stretch', sm: 'center' }
              }}>
                <Autocomplete
                  options={classes}
                  getOptionLabel={(option) => option ? `Class ${option.name}${option.section ? ` - ${option.section}` : ''}` : ''}
                  value={classes.find((cls) => cls.id === selectedClass) || null}
                  onChange={(event, newValue) => {
                    setSelectedClass(newValue ? newValue.id : '');
                    setSelectedStudent('');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Class"
                      placeholder="Search and select class..."
                      size={isMobile ? 'small' : 'medium'}
                    />
                  )}
                  sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No classes found"
                />

                <Autocomplete
                  options={students}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const rollNo = option.roll_number ? `${option.roll_number} - ` : '';
                    return `${rollNo}${option.full_name}`;
                  }}
                  value={students.find((student) => student.id === selectedStudent) || null}
                  onChange={(event, newValue) => {
                    setSelectedStudent(newValue ? newValue.id : '');
                  }}
                  disabled={!selectedClass}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Student"
                      placeholder="Search and select student..."
                      size={isMobile ? 'small' : 'medium'}
                    />
                  )}
                  sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No students found"
                />
              </Box>
            </Paper>

            {/* Feedback List */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : feedback.length === 0 ? (
              <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No feedback found
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                {feedback.map((item) => (
                  <Card
                    key={item.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: 2,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Stack spacing={1.5}>
                        {/* Top Row: Student, Teacher, Status - Horizontal */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 2 },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          flexWrap: 'wrap'
                        }}>
                          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}
                            >
                              Student
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                              noWrap
                            >
                              {item.roll_number ? `${item.roll_number} - ` : ''}
                              {item.student_name || 'N/A'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}
                            >
                              Teacher/Admin
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                              noWrap
                            >
                              {item.teacher_name || 'N/A'}
                            </Typography>
                          </Box>

                          <Chip
                            label={item.is_read == 1 || item.is_read === true ? 'Read' : 'Unread'}
                            color={item.is_read == 1 || item.is_read === true ? 'success' : 'warning'}
                            size="small"
                            sx={{ 
                              alignSelf: { xs: 'flex-start', sm: 'center' },
                              height: 24,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>

                        {/* Message Row - Compact */}
                        <Box>
                          <Tooltip
                            title={item.message}
                            arrow
                            placement="top"
                            enterDelay={300}
                            leaveDelay={100}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                color: 'text.primary',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                cursor: 'help',
                              }}
                            >
                              {item.message}
                            </Typography>
                          </Tooltip>
                        </Box>

                        {/* Bottom Row: Date and Actions - Horizontal */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1, sm: 1.5 },
                          pt: 0.5
                        }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {formatDateTime(item.created_at)}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            flexWrap: 'wrap',
                            width: { xs: '100%', sm: 'auto' }
                          }}>
                            {/* Edit button - Teachers can edit their own feedback, Admins can edit any */}
                            {(isAdmin() || item.created_by === currentUserId) && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => handleEditFeedback(item)}
                                sx={{ 
                                  textTransform: 'none',
                                  flex: { xs: 1, sm: 'none' },
                                  minWidth: { xs: 0, sm: 'auto' },
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                  px: 1.5
                                }}
                              >
                                Edit
                              </Button>
                            )}
                            {/* Delete button - Only Admins can delete */}
                            {isAdmin() && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteFeedbackClick(item.id)}
                                sx={{ 
                                  textTransform: 'none',
                                  flex: { xs: 1, sm: 'none' },
                                  minWidth: { xs: 0, sm: 'auto' },
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                  px: 1.5
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Infinite scroll sentinel */}
                {hasMore && (
                  <Box
                    ref={sentinelRef}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 2,
                      minHeight: 60,
                    }}
                  >
                    {isAppending && <CircularProgress size={24} />}
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      {/* Share Feedback Modal */}
      <Dialog
        open={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setShareFormData({ class_id: '', student_id: '', message: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Autocomplete
              options={classes}
              getOptionLabel={(option) => option ? `Class ${option.name}${option.section ? ` - ${option.section}` : ''}` : ''}
              value={classes.find((cls) => cls.id === shareFormData.class_id) || null}
              onChange={(event, newValue) => {
                setShareFormData({
                  ...shareFormData,
                  class_id: newValue ? newValue.id : '',
                  student_id: '', // Reset student when class changes
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Class"
                  placeholder="Search and select class..."
                />
              )}
              sx={{ maxHeight: 56 }}
              ListboxProps={{
                style: {
                  maxHeight: 200,
                },
              }}
              noOptionsText="No classes found"
            />

            <Autocomplete
              options={shareModalStudents}
              getOptionLabel={(option) => {
                if (!option) return '';
                const rollNo = option.roll_number ? `${option.roll_number} - ` : '';
                return `${rollNo}${option.full_name}`;
              }}
              value={shareModalStudents.find((student) => student.id === shareFormData.student_id) || null}
              onChange={(event, newValue) => {
                setShareFormData({
                  ...shareFormData,
                  student_id: newValue ? newValue.id : '',
                });
              }}
              disabled={!shareFormData.class_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Student"
                  placeholder="Search and select student..."
                />
              )}
              sx={{ maxHeight: 56 }}
              ListboxProps={{
                style: {
                  maxHeight: 200,
                },
              }}
              noOptionsText="No students found"
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Feedback Message"
              value={shareFormData.message}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 250) {
                  setShareFormData({ ...shareFormData, message: value });
                }
              }}
              placeholder="Enter your feedback message..."
              inputProps={{ maxLength: 250 }}
              helperText={`${shareFormData.message.length}/250 characters`}
              FormHelperTextProps={{
                sx: {
                  textAlign: 'right',
                  mx: 0,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShareModalOpen(false);
              setShareFormData({ class_id: '', student_id: '', message: '' });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleShareFeedback}
            disabled={shareLoading || !shareFormData.class_id || !shareFormData.student_id || !shareFormData.message.trim()}
          >
            {shareLoading ? <CircularProgress size={20} /> : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Feedback Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingFeedback(null);
          setEditFormData({ message: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Feedback Message"
              value={editFormData.message}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 250) {
                  setEditFormData({ ...editFormData, message: value });
                }
              }}
              placeholder="Enter your feedback message..."
              inputProps={{ maxLength: 250 }}
              helperText={`${editFormData.message.length}/250 characters`}
              FormHelperTextProps={{
                sx: {
                  textAlign: 'right',
                  mx: 0,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditModalOpen(false);
              setEditingFeedback(null);
              setEditFormData({ message: '' });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateFeedback}
            disabled={editLoading || !editFormData.message.trim()}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, feedbackId: null })}
        onConfirm={() => handleDeleteFeedback(confirmDialog.feedbackId)}
        title="Delete Feedback"
        message="Are you sure you want to delete this feedback? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        severity="error"
      />
    </>
  );
};

export default Feedback;

